import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { readCachedTheme, ThemeSync, type ThemeCache, type ThemeStore } from "./themeSync";
import { defaultTheme, offeredThemes, type THEMES, type ThemeName } from "./themes";

export type { ThemeName } from "./themes";

interface ThemeState {
  theme: ThemeName;
  /** Every theme on offer here, in the order a picker lists them (a preview theme only where the server offers it). */
  themes: (typeof THEMES)[number][];
  /** The person choosing a theme: applied at once and, when signed in, kept with their account. */
  setTheme: (theme: ThemeName) => void;
  /** Wire the signed-in account in (or out, with null) — see `ThemeAccountSync`. */
  bindAccount: (store: ThemeStore | null) => void;
}

const ThemeContext = createContext<ThemeState | undefined>(undefined);

function browserCache(): ThemeCache | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function prefersDark(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * The app theme (KEY-755). Outside the auth provider, so the sign-in page is themed too: it paints from the
 * browser cache (or the OS preference on a first visit) and is then corrected to the signed-in person's stored
 * theme by `ThemeAccountSync`, which lives inside the auth provider.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [chosen, setThemeState] = useState<ThemeName>(() => readCachedTheme(browserCache(), prefersDark()));
  const [available, setAvailable] = useState<readonly string[] | null>(null);
  const syncRef = useRef<ThemeSync | null>(null);
  if (!syncRef.current) {
    syncRef.current = new ThemeSync(setThemeState, browserCache(), setAvailable);
  }

  // A cached or stored theme that isn't offered here (Soft on prod) paints as the default instead.
  const themes = useMemo(() => offeredThemes(available), [available]);
  const theme: ThemeName = themes.some((t) => t.id === chosen) ? chosen : defaultTheme(prefersDark());

  // The dial surfaces are token-driven: switching themes is a single attribute
  // swap on <html> that re-resolves every CSS custom property instantly.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemeName) => {
    void syncRef.current!.pick(next);
  }, []);

  const bindAccount = useCallback((store: ThemeStore | null) => {
    if (store) {
      void syncRef.current!.bind(store);
    } else {
      syncRef.current!.unbind();
    }
  }, []);

  const value = useMemo<ThemeState>(() => ({ theme, themes, setTheme, bindAccount }), [theme, themes, setTheme, bindAccount]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
